import { captureError } from '@smg-real-estate/browser-rum';
import { MandateState, Status, type SellerLead } from '@smg-real-estate/seller-leads-schema';

import { createRequest } from '@/api/fetch';
import {
  Sort,
  type LeadsRequestBody,
  type LeadsResponseBody,
  type SearchFilters,
} from '@/models/sellerLead';

export interface AgencySelectedDistrict {
  district: string;
  postalCodeIds: string[];
}

export interface OptionalPayload {
  mandateState?: MandateState;
  commentNoMandateNotSold?: string;
  targetSellingPrice?: number;
  brokerCommission?: number;
  commissionType?: 'percentage' | 'fixedFee';
  finalSellingPrice?: number;
}

interface StatusPayload extends OptionalPayload {
  status: Status;
}

export const PAGE_SIZE = 10;
export const INITIAL_PAGE = 1;

export const getLeads = async (
  page = INITIAL_PAGE,
  filters?: SearchFilters,
): Promise<LeadsResponseBody | null> => {
  try {
    const requestParams: LeadsRequestBody = {
      page,
      pageSize: PAGE_SIZE,
      filters: filters || { sortOrder: Sort.Desc },
    };
    const response = await createRequest({ method: 'POST', credentials: 'omit' })(
      `${import.meta.env.VUE_APP_SELLER_LEAD_MANAGE_API_BASE_URL}/leads`,
      JSON.stringify(requestParams),
    );

    if (!response.ok) {
      captureError(new Error(`Failed to get seller leads with response code: ${response.status}`));
      return null;
    }
    return await response.json();
  } catch (error) {
    captureError(error);
    return null;
  }
};

export const getLeadById = async (
  leadId: string,
): Promise<SellerLead | { statusCode: number; results: SellerLead | null } | null> => {
  try {
    const response = await createRequest({ method: 'GET', credentials: 'omit' })(
      `${import.meta.env.VUE_APP_SELLER_LEAD_MANAGE_API_BASE_URL}/leads/${encodeURIComponent(leadId)}`,
    );

    if (!response.ok) {
      const message = `Failed to get seller lead with lead id: ${leadId} and response code: ${response.status}`;
      captureError(new Error(message));
      return { statusCode: response.status, results: null };
    }
    return {
      statusCode: response.status,
      results: (await response.json()) as unknown as SellerLead,
    };
  } catch (error) {
    captureError(error);
    return { statusCode: error.statusCode, results: null };
  }
};

export const refundRequest = async (
  leadId: string,
  refundReason: string,
  agencyComment: string,
): Promise<boolean> => {
  try {
    const requestParams = JSON.stringify({ refundReason, agencyComment });
    const response = await createRequest({ method: 'POST', credentials: 'omit' })(
      `${import.meta.env.VUE_APP_SELLER_LEAD_MANAGE_API_BASE_URL}/agency-leads/${encodeURIComponent(leadId)}/refund`,
      requestParams,
    );

    if (!response.ok) {
      captureError(
        new Error(
          `Failed to generate refund request with lead id: ${leadId} and response code: ${response.status}`,
        ),
      );
      return false;
    }
    return true;
  } catch (error) {
    captureError(error);
    return false;
  }
};

export const updateStatus = async (
  leadId: string,
  status: Status,
  optionalPayload?: OptionalPayload,
): Promise<boolean> => {
  try {
    const payload: StatusPayload = {
      status: status,
      ...optionalPayload,
    };

    const response = await createRequest({ method: 'PUT', credentials: 'omit' })(
      `${import.meta.env.VUE_APP_SELLER_LEAD_MANAGE_API_BASE_URL}/agency-leads/${encodeURIComponent(leadId)}/status`,
      JSON.stringify(payload),
    );

    if (!response.ok) {
      captureError(
        new Error(
          `Failed to update status of lead with lead id: ${leadId} and response code: ${response.status}`,
        ),
      );
      return false;
    }
    return true;
  } catch (error) {
    captureError(error);
    return false;
  }
};

export const getAgencySelectedDistricts = async (): Promise<AgencySelectedDistrict[] | null> => {
  try {
    const response = await createRequest({ method: 'GET', credentials: 'omit' })(
      `${import.meta.env.VUE_APP_SELLER_LEAD_MANAGE_API_BASE_URL}/agency-lead-region`,
    );

    if (!response.ok) {
      captureError(
        new Error(`Failed to get agency selected districts with response code: ${response.status}`),
      );
      return null;
    }
    return (await response.json()) as AgencySelectedDistrict[];
  } catch (error) {
    captureError(error);
    return null;
  }
};

export const updateAgencyLeadRegion = async (postalCodeIds: string[]): Promise<boolean> => {
  try {
    const response = await createRequest({ method: 'PUT', credentials: 'omit' })(
      `${import.meta.env.VUE_APP_SELLER_LEAD_MANAGE_API_BASE_URL}/agency-lead-region`,
      JSON.stringify({ postalCodeIds }),
    );

    if (!response.ok) {
      captureError(
        new Error(`Failed to update agency lead region with response code: ${response.status}`),
      );
      return false;
    }

    return true;
  } catch (error) {
    captureError(error);
    return false;
  }
};
